import React from 'react';
import {Box, Typography} from '@mui/material';
import {FieldInputProps} from 'formik';
import {Pot} from '../../../schema-types/Pot';
import {withdrawalFieldsType} from './builderWithdrawalFields';
import {getSummary} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {isBankAccount} from '../../../utils/potsFilter';
import {BuilderCurrentAccountDetails} from './BuilderCurrentAccountDetails';
import {NewBuilderCurrentAccountDetails} from './BuilderNewCurrentAccountDetails';
import {NEW_BANK_ACCOUNT_ID} from '../CreateBuilder/BuilderSourceDestination/builderConstants';
import {BuilderPlanForm} from '../CreateBuilder/BuilderSourceDestination/BuilderPlanForm';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';

export interface BuilderWithdrawalInfoCaptureProps {
  // Withdrawal (bank account linked to nominated account)
  linked?: boolean;
  // Withdrawal (bank account validated)
  validatedBank?: boolean;
  // Withdrawal (bank account needs linking)
  needsLink?: boolean;
}

const WithdrawalExtraText: React.FC<{
  pot: Pot;
  withdrawalFieldObj: FieldInputProps<withdrawalFieldsType | undefined>;
}> = ({pot, withdrawalFieldObj}) => {
  const {id, totalValue} = pot;
  if (id !== withdrawalFieldObj?.value?.destination?.id || isBankAccount(pot)) {
    return null;
  }
  return (
    <Typography>
      <strong data-testid="withdrawal-contribution-id">{` - Post Contribution: ${getCurrencyString(
        getCurrencyFloat(totalValue) +
          getCurrencyFloat(withdrawalFieldObj?.value?.source?.singleClientAmount || 0)
      )}`}</strong>
    </Typography>
  );
};

interface BuilderWithdrawalDestinationPlanFormProps {
  pot: Pot;
  withdrawalFieldObj: FieldInputProps<withdrawalFieldsType | undefined>;
  withdrawalPot?: Pot | null;
  isSelected: boolean;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const BuilderWithdrawalDestinationPlanForm: React.FC<
  BuilderWithdrawalDestinationPlanFormProps
> = ({pot, withdrawalFieldObj, withdrawalPot, isSelected, handleChange}) => {
  return (
    <BuilderPlanForm
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      details={<BuilderCurrentAccountDetails currentAccount={pot} />}
      summary={
        <Box display="flex" flexDirection="row">
          {getSummary(pot)}
          &nbsp;
          <WithdrawalExtraText pot={pot} withdrawalFieldObj={withdrawalFieldObj} />
        </Box>
      }
      curoId={pot.curoId}
      withdrawalValidation={{
        needsLink: isBankAccount(pot),
        linked: withdrawalPot?.nominatedAccount?.id === pot.id,
        validatedBank: pot.isValidated ?? undefined,
      }}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};

interface BuilderWithdrawalNewAccountPlanFormProps {
  isSelected: boolean;
  handleChange: () => void;
}

export const BuilderWithdrawalNewAccountPlanForm: React.FC<
  BuilderWithdrawalNewAccountPlanFormProps
> = ({isSelected, handleChange}) => {
  return (
    <BuilderPlanForm
      id={NEW_BANK_ACCOUNT_ID}
      reccId={null}
      summary={<Typography>New Current Account</Typography>}
      details={<NewBuilderCurrentAccountDetails />}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};
